export const generateSmartTitle = (userMessage) => {
  if (!userMessage || typeof userMessage !== 'string') {
    return `Chat ${new Date().toLocaleString()}`;
  }
  
  if (userMessage.length <= 50) {
    return userMessage;
  }
  
  const sentences = userMessage.split(/[.!?]+/);
  const firstSentence = sentences[0];
  
  if (firstSentence && firstSentence.length <= 50) {
    return firstSentence;
  }
  
  const words = userMessage.split(/\s+/);
  const truncated = words.slice(0, 8).join(' ');
  
  return truncated.length < userMessage.length ? `${truncated}...` : truncated;
};

export const extractInlineToolJson = (text) => {
  if (!text || typeof text !== 'string') {
    return { cleanedText: text, tools: [] };
  }

  const toolJsonRegex = /\{[^{}]*"tool_name"\s*:\s*"[^"]+"\s*[^{}]*\}/g;
  const matches = [...text.matchAll(toolJsonRegex)];
  
  if (matches.length === 0) {
    return { cleanedText: text, tools: [] };
  }

  const toolMap = new Map();
  let cleanedText = text;

  for (const match of matches) {
    try {
      const parsed = JSON.parse(match[0]);
      if (parsed && parsed.tool_name) {
        const toolName = parsed.tool_name;
        
        if (!toolMap.has(toolName)) {
          toolMap.set(toolName, {
            tool_name: toolName,
            description: parsed.description || '',
            start_time: null,
            end_time: null,
            status: 'calling'
          });
        }
        
        const tool = toolMap.get(toolName);
        
        if (parsed.start_time) {
          tool.start_time = parsed.start_time;
          tool.status = 'in_progress';
        }
        if (parsed.description && !tool.description) {
          tool.description = parsed.description;
        }
        if (parsed.end_time) {
          tool.end_time = parsed.end_time;
          tool.status = 'completed';
          tool.completed = true;
        }
        
        toolMap.set(toolName, tool);
      }
    } catch (e) {
    }
    
    cleanedText = cleanedText.replace(match[0], '');
  }

  cleanedText = cleanedText.replace(/\s{2,}/g, ' ').trim();

  const tools = Array.from(toolMap.values());
  
  return { cleanedText, tools };
};

export const formatModelName = (modelKey) => {
  const modelName = modelKey.split('/').pop() || modelKey;
  
  return modelName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export const extractReferences = (text) => {
  const refRegex = /<ref>(.*?)<\/ref>/gs;
  const matches = [...text.matchAll(refRegex)];
  const cleanText = text.replace(refRegex, '');
  const references = matches.map(match => match[1].trim());
  return { cleanText, references };
};

export const extractImages = (text) => {
  const imgRegex = /<img[^>]+src=["']([^"']*\.(?:png|jpg|jpeg|gif|webp))["'][^>]*\/?>/gi;
  const matches = [...text.matchAll(imgRegex)];
  const images = matches.map(match => {
    const src = match[1];
    if (src.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(png|jpg|jpeg|gif|webp)$/i)) {
      const altMatch = match[0].match(/alt=["']([^"']*)["']/i);
      const alt = altMatch ? altMatch[1] : '';
      return { filename: src, alt };
    }
    return null;
  }).filter(Boolean);
  const cleanedText = text.replace(imgRegex, '');
  return { cleanedText, images };
};

export const processMessageForInlineRendering = (text, showReferences = true) => {
  let processedText = text;
  let references = [];
  let images = [];
  let tools = [];

  const messageId = extractMessageId(processedText);

  const toolExtraction = extractInlineToolJson(processedText);
  processedText = toolExtraction.cleanedText;
  tools = toolExtraction.tools;

  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*\/?>/gi;
  const imgMatches = [...processedText.matchAll(imgRegex)];

  [...imgMatches].reverse().forEach((match, index) => {
    const src = match[1];
    const isUuidFilename = src.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(png|jpg|jpeg|gif|webp)$/i);
    const isUuidPath = src.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(png|jpg|jpeg|gif|webp)$/i);
    
    if (isUuidFilename || isUuidPath) {
      const actualIndex = imgMatches.length - 1 - index;
      const altMatch = match[0].match(/alt=["']([^"']*)["']/i);
      const alt = altMatch ? altMatch[1] : '';
      const filename = isUuidFilename ? src : src.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(png|jpg|jpeg|gif|webp)$/i)[0];
      images.unshift({ filename, alt });
      processedText = processedText.replace(match[0], `[IMAGE_${actualIndex}]`);
    }
  });

  if (showReferences) {
    const refRegex = /<ref>(.*?)<\/ref>/gs;
    const refMatches = [...processedText.matchAll(refRegex)];

    [...refMatches].reverse().forEach((match, index) => {
      const actualIndex = refMatches.length - 1 - index;
      references.unshift(match[1].trim());
      processedText = processedText.replace(match[0], `[^${actualIndex}]`);
    });
  } else {
    processedText = processedText.replace(/<ref>.*?<\/ref>/gs, '');
  }

  processedText = processedText.replace(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/g, '');

  return {
    processedText: processedText,
    references,
    images,
    tools,
    imageFilenames: images.map(img => img.filename),
    messageId
  };
};

export const extractMessageId = (text) => {
  const idRegex = /([0-9a-f]{8}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{12})/i;
  const idMatch = text.match(idRegex);
  if (idMatch) {
    return idMatch[1];
  }
  return null;
};


export const cleanAiMessageText = (text, model, showReferences) => {
  let cleanText = text;

  const messageId = extractMessageId(cleanText);
  const idRegex = /([0-9a-f]{8}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{4}[\s-]?[0-9a-f]{12})/gi;

  const imageExtraction = extractImages(cleanText);
  cleanText = imageExtraction.cleanedText.replace(idRegex, '');

  if (!showReferences) {
    cleanText = cleanText.replace(/<ref>.*?<\/ref>/gs, '');
  }

  return { cleanText, messageId, images: imageExtraction.images, imageFilenames: imageExtraction.images.map(img => img.filename) };
};
